import React, { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  Plus,
  Search,
  MoreVertical,
  Edit,
  Trash2,
  Mail,
  Phone,
  MapPin,
  Users,
  FileText,
  Eye,
  X,
  Copy,
  ExternalLink
} from 'lucide-react'
import { useApp } from '../context/AppContext'
import ClientModal from '../components/ClientModal'

export default function Clients() {
  const { clients, invoices, addClient, updateClient, deleteClient, getInvoiceById, addNotification } = useApp()
  const [searchQuery, setSearchQuery] = useState('')
  const [showModal, setShowModal] = useState(false)
  const [editingClient, setEditingClient] = useState(null)
  const [showActions, setShowActions] = useState(null)
  const navigate = useNavigate()

  // Filter clients
  const filteredClients = clients.filter(client => {
    if (!searchQuery) return true
    const query = searchQuery.toLowerCase()
    return (
      client.name.toLowerCase().includes(query) ||
      client.email.toLowerCase().includes(query) ||
      (client.phone && client.phone.toLowerCase().includes(query))
    )
  })

  // Get client stats
  const getClientStats = (clientId) => {
    const clientInvoices = invoices.filter(inv => inv.clientId === clientId)
    const totalBilled = clientInvoices.reduce((sum, inv) => sum + inv.total, 0)
    const paidAmount = clientInvoices
      .filter(inv => inv.status === 'paid')
      .reduce((sum, inv) => sum + inv.total, 0)
    const outstanding = totalBilled - paidAmount
    const invoiceCount = clientInvoices.length

    return { totalBilled, outstanding, invoiceCount }
  }

  const handleDelete = (client) => {
    const clientInvoices = invoices.filter(inv => inv.clientId === client.id)
    if (clientInvoices.length > 0) {
      addNotification(`Cannot delete client with ${clientInvoices.length} associated invoices`, 'error')
      return
    }

    if (window.confirm(`Are you sure you want to delete ${client.name}?`)) {
      deleteClient(client.id)
      addNotification('Client deleted successfully', 'success')
    }
    setShowActions(null)
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Clients</h2>
          <p className="text-gray-600 mt-1">Manage your client relationships</p>
        </div>
        <button
          onClick={() => {
            setEditingClient(null)
            setShowModal(true)
          }}
          className="btn btn-primary"
        >
          <Plus size={20} />
          <span>Add Client</span>
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="card p-5">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center">
              <Users size={24} className="text-primary-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{clients.length}</p>
              <p className="text-sm text-gray-500">Total Clients</p>
            </div>
          </div>
        </div>
        <div className="card p-5">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-accent-100 flex items-center justify-center">
              <FileText size={24} className="text-accent-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{invoices.length}</p>
              <p className="text-sm text-gray-500">Total Invoices</p>
            </div>
          </div>
        </div>
        <div className="card p-5">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
              <Mail size={24} className="text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">
                {clients.filter(c => c.email).length}
              </p>
              <p className="text-sm text-gray-500">With Email</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="card p-4">
        <div className="relative max-w-md">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search clients..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="input pl-10"
          />
        </div>
      </div>

      {/* Clients Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredClients.length === 0 ? (
          <div className="col-span-full card p-12 text-center">
            <Users size={48} className="mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500">No clients found</p>
            {searchQuery && (
              <p className="text-sm text-gray-400 mt-1">Try adjusting your search</p>
            )}
            <button
              onClick={() => {
                setEditingClient(null)
                setShowModal(true)
              }}
              className="btn btn-primary mt-4 inline-flex"
            >
              <Plus size={18} />
              Add Your First Client
            </button>
          </div>
        ) : (
          filteredClients.map((client) => {
            const stats = getClientStats(client.id)

            return (
              <div key={client.id} className="card hover:shadow-md transition-shadow">
                <div className="p-5">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary-100 to-primary-200 flex items-center justify-center">
                        <span className="text-primary-700 font-bold text-lg">
                          {client.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <Link to={`/clients/${client.id}`} className="hover:underline">
                          <h3 className="font-semibold text-gray-900">{client.name}</h3>
                        </Link>
                        <p className="text-sm text-gray-500">{client.email}</p>
                      </div>
                    </div>
                    <div className="relative">
                      <button
                        onClick={() => setShowActions(showActions === client.id ? null : client.id)}
                        className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                      >
                        <MoreVertical size={18} className="text-gray-500" />
                      </button>
                      {showActions === client.id && (
                        <>
                          <div
                            className="fixed inset-0 z-10"
                            onClick={() => setShowActions(null)}
                          />
                          <div className="absolute right-0 mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-100 py-1 z-20 animate-scale-in">
                            <button
                              onClick={() => {
                                navigate(`/clients/${client.id}`)
                                setShowActions(null)
                              }}
                              className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 w-full"
                            >
                              <Eye size={14} />
                              View Details
                            </button>
                            <button
                              onClick={() => {
                                const link = `${window.location.origin}/portal/client/${client.id}`
                                navigator.clipboard.writeText(link)
                                addNotification('Portal link copied to clipboard', 'success')
                                setShowActions(null)
                              }}
                              className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 w-full"
                            >
                              <Copy size={14} />
                              Copy Portal Link
                            </button>
                            <button
                              onClick={() => {
                                setEditingClient(client)
                                setShowModal(true)
                                setShowActions(null)
                              }}
                              className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 w-full"
                            >
                              <Edit size={14} />
                              Edit
                            </button>
                            <button
                              onClick={() => handleDelete(client)}
                              className="flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 w-full"
                            >
                              <Trash2 size={14} />
                              Delete
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Contact Info */}
                  <div className="space-y-2 mb-4">
                    {client.phone && (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Phone size={14} className="text-gray-400" />
                        {client.phone}
                      </div>
                    )}
                    {client.address && (
                      <div className="flex items-start gap-2 text-sm text-gray-600">
                        <MapPin size={14} className="text-gray-400 mt-0.5" />
                        <span className="line-clamp-2">{client.address}</span>
                      </div>
                    )}
                  </div>

                  {/* Stats */}
                  <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-100">
                    <div>
                      <p className="text-lg font-semibold text-gray-900">
                        ${stats.totalBilled.toLocaleString()}
                      </p>
                      <p className="text-xs text-gray-500">Total Billed</p>
                    </div>
                    <div>
                      <p className={`text-lg font-semibold ${stats.outstanding > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                        ${stats.outstanding.toLocaleString()}
                      </p>
                      <p className="text-xs text-gray-500">Outstanding</p>
                    </div>
                  </div>

                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <p className="text-sm text-gray-500">
                      {stats.invoiceCount} invoice{stats.invoiceCount !== 1 ? 's' : ''} •
                      Client since {client.createdAt ? new Date(client.createdAt?.toDate?.() || client.createdAt).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : 'N/A'}
                    </p>
                  </div>
                </div>
              </div>
            )
          })
        )}
      </div>

      {/* Client Modal */}
      {showModal && (
        <ClientModal
          client={editingClient}
          onClose={() => {
            setShowModal(false)
            setEditingClient(null)
          }}
          onSave={(clientData) => {
            if (editingClient) {
              updateClient(editingClient.id, clientData)
              addNotification('Client updated successfully', 'success')
            } else {
              addClient(clientData)
              addNotification('Client added successfully', 'success')
            }
            setShowModal(false)
            setEditingClient(null)
          }}
        />
      )}
    </div>
  )
}
